import { css } from "@emotion/react";
import { Badge } from "@/components/ui/badge";
import type { PackageItem, Product } from "@/hooks/useInventoryState";
import { useShoppingCartItemsState } from "@/hooks/useShoppingCartItemsState";
import { useThemeState } from "@/hooks/useThemeState";
import { useTranslate } from "@/hooks/useTranslate/useTranslate";
import FwFunc from "@/lib/FwReact/FwFunc";
import { FwNumberDropDown } from "@/lib/FwReact/FwNumberDropDown";
import type { ImmutableObject } from "@/lib/immutable";
import type { ProductDetailsViewSettings } from "../../ProductDetails";
import { ProductDetailsPackageItemAvailability } from "../PackageItemFields/ProductDetailsPackageItemAvailability";
import { ProductDetailsPackageItemImage } from "../PackageItemFields/ProductDetailsPackageItemImage";

//------------------------------------------------------------------------------------
export type ProductDetailsPackageItemProps = {
	packageProduct: ImmutableObject<Product>;
	packageItem: ImmutableObject<PackageItem>;
	allowAddingUnavailableItems: boolean;
	enableQty: boolean;
	viewSettings: ProductDetailsViewSettings;
};
export const ProductDetailsPackageItem: React.FC<
	ProductDetailsPackageItemProps
> = (props) => {
	const packageProduct = props.packageProduct;
	const isAvailable = (packageProduct.Availability?.TotalIn ?? 0) > 0;
	const canAddToCart =
		props.enableQty && (isAvailable || props.allowAddingUnavailableItems);
	const item = props.packageItem;
	const shoppingCartItemsState = useShoppingCartItemsState();
	const themeState = useThemeState();
	const translate = useTranslate();
	const isDarkTheme = themeState.isDark();
	const showImage = item.Images && item.Images.length > 0;
	const showAvailability = item.Availability !== undefined;
	const showDailyRate =
		props.viewSettings.hasRates && props.viewSettings.hasDailyRate;
	const showWeeklyRate =
		props.viewSettings.hasRates && props.viewSettings.hasWeeklyRate;
	const showMonthlyRate =
		props.viewSettings.hasRates && props.viewSettings.hasMonthlyRate;

	const handleChangeQtyAsync = async (value: number): Promise<void> => {
		return Promise.resolve().then(() => {
			shoppingCartItemsState.setPackageItemQty(
				props.packageProduct,
				props.packageItem,
				value,
			);
		});
	};

	return (
		<div
			css={css`
				border: 1px solid rgba(148, 163, 184, 0.35);
				border-radius: 12px;
				padding: 0.65rem;
				background: ${
					isDarkTheme ? "rgba(0, 0, 0, 0.18)" : "rgba(255, 255, 255, 0.72)"
				};
				display: grid;
				grid-template-columns: ${showImage ? "96px minmax(0, 1fr)" : "minmax(0, 1fr)"};
				gap: 0.65rem;
				align-items: start;
				@media print {
					background: transparent;
				}
			`}
		>
			{showImage && (
				<div
					css={css`
						grid-row: span 2;
						min-width: 0;
						width: 96px;
					`}
				>
					<ProductDetailsPackageItemImage
						product={packageProduct}
						packageItem={item}
					/>
				</div>
			)}
			<div
				css={css`
					min-width: 0;
					display: grid;
					gap: 0.5rem;
				`}
			>
				<div
					css={css`
						display: flex;
						flex-wrap: wrap;
						align-items: flex-start;
						justify-content: space-between;
						gap: 0.5rem;
					`}
				>
					<div
						css={css`
							min-width: 0;
							flex: 1 1 14rem;
							font-size: 0.82rem;
							line-height: 1.3;
							color: ${isDarkTheme ? "#e5e7eb" : "inherit"};
						`}
					>
						{item.Description}
					</div>
					<div
						css={css`
							display: flex;
							flex-wrap: wrap;
							justify-content: flex-end;
							gap: 0.35rem;
						`}
					>
						{item.IsOption && (
							<Badge className="border-yellow-700 bg-yellow-300 text-black">
								{translate("Optional accessories")}
							</Badge>
						)}
						<Badge className="border-orange-700 bg-orange-300 text-black">
							{`x${item.DefaultQuantity}`}
						</Badge>
					</div>
				</div>
				{props.viewSettings.hasRates && (
					<div
						css={css`
							display: flex;
							flex-wrap: wrap;
							gap: 0.35rem 0.75rem;
							font-size: 0.72rem;
							color: ${isDarkTheme ? "#f3f4f6" : "#475569"};
						`}
					>
						{showDailyRate && (
							<div>{`${translate("Daily")}: ${item.CurrencySymbol}${FwFunc.numberFormatter(item.DailyRate, 2, ",", ".")}`}</div>
						)}
						{showWeeklyRate && (
							<div>{`${translate("Weekly")}: ${item.CurrencySymbol}${FwFunc.numberFormatter(item.WeeklyRate, 2, ",", ".")}`}</div>
						)}
						{showMonthlyRate && (
							<div>{`${translate("Monthly")}: ${item.CurrencySymbol}${FwFunc.numberFormatter(item.MonthlyRate, 2, ",", ".")}`}</div>
						)}
					</div>
				)}
				{(canAddToCart || showAvailability) && (
					<div
						css={css`
							display: flex;
							flex-wrap: wrap;
							align-items: center;
							gap: 0.5rem 0.75rem;
						`}
					>
						{canAddToCart &&
							(item.IsOption ? (
								<div
									css={css`
										flex: 0 0 auto;
										min-width: 5.25rem;
									`}
								>
									<FwNumberDropDown
										css={css`flex:1 1 0;`}
										label={translate("Qty")}
										value={shoppingCartItemsState.getPackageItemQty(
											props.packageProduct,
											item,
										)}
										onChange={(newValue) => {
											handleChangeQtyAsync(newValue);
										}}
									/>
								</div>
							) : (
								<div
									css={css`
										font-size: 0.76rem;
										font-weight: 600;
										color: ${isDarkTheme ? "#f3f4f6" : "#475569"};
									`}
								>
									{`${translate("Qty")}: ${item.DefaultQuantity}`}
								</div>
							))}
						{showAvailability && (
							<div
								css={css`
									display: flex;
									align-items: center;
									min-width: 0;
								`}
							>
								<ProductDetailsPackageItemAvailability packageItem={item} />
							</div>
						)}
					</div>
				)}
			</div>
		</div>
	);
};
